import { FilterGroupType } from '../Components/ProductFiltering/types/FilterGroupType';
import {
  PRODUCT_FILTERING_RECEIVE,
  PRODUCT_FILTERING_SET_SORTING,
  PRODUCT_FILTERING_UPDATE_FILTERS,
  PRODUCT_FILTERING_RECEIVE_BRAND_CATEGORY_FILTERS,
} from '../constants';
import { get } from '../Services/http';
import {
  setIsLoadingProductListing,
  setProducts,
} from './ProductListing.action';

let abortController = null;

export const queryProductFiltering = (
  productFilteringState = null,
  isInitialLoad = false,
  isPageLoad = false
) => async (dispatch, getState) => {
  try {
    productFilteringState ??= getState().productFiltering;
    const productListingState = getState().productListing;

    // Show skeleton cards before loading the new products
    if (!isInitialLoad) {
      dispatch(setIsLoadingProductListing(true));
      dispatch(
        setProducts(Array.from({ length: productListingState.pageSize }))
      );
    }

    // Cancel the last request
    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();

    let queryString = '';

    if (isPageLoad) {
      // Get the query string from the current URL
      queryString = window.location.search ? window.location.search.substring(1) : '';
    } else {
      queryString = getProductFilteringAsQueryString(
        productFilteringState,
        productListingState
      );
    }

    const response = await get(
      `/api/productfiltering?${queryString}`,
      abortController
    );

    if (response.ok) {
      const isJson = response.headers
        .get('content-type')
        ?.includes('application/json');
      if (isJson) {
        let data = await response.json();
        if (isInitialLoad) {
          delete data.productListing;
        }

        dispatch(receiveProductFiltering(data));
      }
    }
  } catch (err) {
    // TODO Error handling, not in MOX-86
    console.log(err);
  } finally {
    dispatch(setIsLoadingProductListing(false));
  }
};

const toFilterCriteria = (groups) =>
  groups
    .map((group) =>
      group.selectedOptions
        .filter((val) =>
          group.asSlider
            ? group.selectedOptions.length == 2
            : val && val.length > 0
        )
        .map(
          (val) =>
            `${encodeURIComponent(group.id)}=${encodeURIComponent(val)}`
        )
    )
    .reduce((flat, current) => [...flat, ...current], []);

export const receiveProductFiltering = (productFiltering) => ({
  type: PRODUCT_FILTERING_RECEIVE,
  payload: productFiltering,
});

// Parent categories are selected to expose the navigation path. Only the
// deepest selected category represents the effective product filter.
const getDeepestSelectedCategory = (categories) => {
  for (const category of categories) {
    if (category.links && Array.isArray(category.links)) {
      const selectedChild = getDeepestSelectedCategory(category.links);
      if (selectedChild) {
        return selectedChild;
      }
    }

    if (category.isSelected) {
      return category;
    }
  }

  return null;
};

export const getProductFilteringAsQueryString = (productFilteringState,
  productListingState = null) => {
  const currentSearchParams = new URLSearchParams(location.search);
  const searchParams = new URLSearchParams();

  // Keep certain params from the current query
  const paramsToKeep = ['page', 'q'];
  paramsToKeep.forEach(
    (key) =>
      currentSearchParams.has(key) &&
      searchParams.append(key, currentSearchParams.get(key))
  );

  // Loop each filter, add each selected option, checkboxes
  productFilteringState.filters
    ?.filter(
      (f) =>
        f.selectedOptions.length > 0 &&
        f.filterGroupType != FilterGroupType.Range
    )
    .forEach((filter) =>
      filter.selectedOptions.forEach((selectedOption) => {
        searchParams.append(filter.id, selectedOption);
      })
    );

  const selectedBrandCategory = getDeepestSelectedCategory(
    productFilteringState.brandCategoryFilters || []
  );
  if (selectedBrandCategory) {
    searchParams.set('category', selectedBrandCategory.systemId);
  }

  // Range filters
  productFilteringState.filters
    ?.filter(
      (f) =>
        f.filterGroupType == FilterGroupType.Range &&
        f.selectedOptions.length == 2
    )
    .forEach((filter) => {
      searchParams.append(
        `${filter.id}`,
        `${filter.selectedOptions[0]}-${filter.selectedOptions[1]}`
      );
    });

  // Sorting
  const selectedSorting = productFilteringState.sortCriteria?.sortItems?.find(
    (s) => s.selected
  );

  if (selectedSorting && selectedSorting.query) {
    searchParams.append('sort_by', selectedSorting.sortBy);
    searchParams.append('sort_direction', selectedSorting.sortDirection);
  }

  // Number to load
  if (productListingState?.productsPerLoadDropdownValue?.value) {
    searchParams.append(
      'products_per_load',
      productListingState.productsPerLoadDropdownValue.value
    );
  }

  return searchParams.toString();
};

export const onOptionChanged = (filterId, optionId) => (
  dispatch,
  getState
) => {
  const filters = [...getState().productFiltering.filters]; // create copy of filters list to not modify the state
  let filterIndex = filters.findIndex((f) => f.id == filterId);

  if (filterIndex == -1) return;

  const { singleSelect, selectedOptions } = filters[filterIndex];
  const optionIndex = selectedOptions.indexOf(optionId);
  const isOptionSelected = optionIndex != -1;
  let updatedSelectedOptions = [];

  if (singleSelect) {
    updatedSelectedOptions = isOptionSelected ? [optionId] : [];
  } else {
    updatedSelectedOptions = isOptionSelected
      ? [
        // selected, remove it from the list
        ...selectedOptions.slice(0, optionIndex),
        ...selectedOptions.slice(optionIndex + 1),
      ]
      : [...selectedOptions, optionId]; // Not selected, add it to selected list
  }
  filters[filterIndex].selectedOptions = updatedSelectedOptions;

  dispatch(updateProductFilteringFilters(filters));
};

export const onRangeChanged = (filterId, values) => (
  dispatch,
  getState
) => {
  const filters = [...getState().productFiltering.filters]; // create copy of filters list to not modify the state
  const filterIndex = filters.findIndex((f) => f.id == filterId);
  if (
    filterIndex == -1 ||
    filters[filterIndex].filterGroupType != FilterGroupType.Range
  )
    return;

  filters[filterIndex].selectedOptions = values;

  dispatch(updateProductFilteringFilters(filters));
};

const updateProductFilteringFilters = (filters) => ({
  type: PRODUCT_FILTERING_UPDATE_FILTERS,
  payload: filters,
});

export const onSortItemSelected = (sortItem) => (dispatch) => {
  dispatch({
    type: PRODUCT_FILTERING_SET_SORTING,
    payload: sortItem,
  });
  dispatch(queryProductFiltering());
};

export const clearAllFilters = () => (dispatch, getState) => {
  const productFilteringState = getState().productFiltering;
  const filtersCopy = Object.assign([], productFilteringState.filters);
  for (let filter of filtersCopy) {
    filter.selectedOptions = [];
  }
  dispatch(
    queryProductFiltering({
      ...productFilteringState,
      filters: filtersCopy,
    })
  );
};

export const clearAllBrandCategoryFilters = () => (
  dispatch,
  getState
) => {
  const productFilteringState = getState().productFiltering;
  const brandCategoryFiltersCopy = productFilteringState.brandCategoryFilters;
  deselectAllInTree(brandCategoryFiltersCopy);
  dispatch({
    type: PRODUCT_FILTERING_RECEIVE_BRAND_CATEGORY_FILTERS,
    payload: brandCategoryFiltersCopy,
  });
  dispatch(fetchAndInitializeCategories());

  dispatch(queryProductFiltering());
};

export const clearFilterGroup = (filterGroupId) => (
  dispatch,
  getState
) => {
  const productFilteringState = getState().productFiltering;
  const filtersCopy = Object.assign([], productFilteringState.filters);
  const filterGroup = filtersCopy.find((f) => f.id == filterGroupId);
  if (!filterGroup) return;
  filterGroup.selectedOptions = [];
  dispatch(updateProductFilteringFilters(filtersCopy));
  dispatch(
    queryProductFiltering({
      ...productFilteringState,
      filters: filtersCopy,
    })
  );
};

// Groups filters by filter id. (Return format: {Color: ["Blå", "Grön"], Weight: [100]}})
const toFilterGroups = (filterArray) => {
  const reducedGroupedFilters = filterArray.reduce((result, filter) => {
    const filterAsKeyValue = filter.split('=');
    const filterName = decodeURIComponent(filterAsKeyValue[0]);
    const value = decodeURIComponent(filterAsKeyValue[1]);

    const prev = result[filterName] ?? [];
    return {
      ...result,
      [filterName]: [...prev, value].sort(),
    };
  }, {});

  return reducedGroupedFilters;
};

// Makes filters easily comparable to mcs filters (Return format: ["Color:Blå,Gul", "Weight:100"])
const toFilterGroupStrings = (reducedGroupedFilters, groups) => {
  const groupedFilters = Object.keys(reducedGroupedFilters).reduce(
    (acc, groupName) => {
      var sortedFilterGroup = reducedGroupedFilters[groupName].sort(
        (a, b) => b - a
      );

      const groupIsRangeType =
        groups.find((group) => group.id == groupName)?.filterGroupType ==
        'Range';

      if (groupIsRangeType) {
        if (sortedFilterGroup.length > 1) {
          const joinedRange = sortedFilterGroup.join('-');
          return [...acc, `${groupName}:${joinedRange}`];
        }
      } else {
        return [...acc, `${groupName}:${sortedFilterGroup.join(';')}`];
      }
    },
    []
  );

  return groupedFilters;
};

export const receiveBrandCategoryFilters = (categories) => (dispatch) => {
  dispatch({
    type: PRODUCT_FILTERING_RECEIVE_BRAND_CATEGORY_FILTERS,
    payload: categories,
  });
};

// Action for fetching and initializing categories
export const fetchAndInitializeCategories = () => async (
  dispatch,
  getState
) => {
  try {

    // Get the current search query parameters from the URL
    const currentSearchParams = new URLSearchParams(window.location.search);

    // Fetch category data
    const response = await get(`/api/productfiltering/brand?${currentSearchParams}`);
    const categories = await response.json();

    // Initialize or update the filter in Redux state
    dispatch(receiveBrandCategoryFilters(categories));

  } catch (error) {
    console.error('Failed to fetch categories:', error);
    return [];
  }
};

// Helper function to find parent of a category
const findCategoryParent = (categories, targetSystemId, parent = null) => {
  for (let cat of categories) {
    if (cat.systemId === targetSystemId) {
      return parent;
    }
    if (cat.links && Array.isArray(cat.links) && cat.links.length > 0) {
      const found = findCategoryParent(cat.links, targetSystemId, cat);
      if (found !== null) {
        return found;
      }
    }
  }
  return null;
};

// Helper function to find a category by systemId
const findCategoryBySystemId = (categories, systemId) => {
  for (let cat of categories) {
    if (cat.systemId === systemId) {
      return cat;
    }
    if (cat.links && Array.isArray(cat.links) && cat.links.length > 0) {
      const found = findCategoryBySystemId(cat.links, systemId);
      if (found) return found;
    }
  }
  return null;
};

// Helper function to check if a category is top-level (has no parent)
const isTopLevelCategory = (categories, systemId) => {
  return categories.some(cat => cat.systemId === systemId);
};

// Helper function to deselect all categories in a tree
const deselectAllInTree = (categories) => {
  categories.forEach(cat => {
    cat.isSelected = false;
    if (cat.links && Array.isArray(cat.links)) {
      deselectAllInTree(cat.links);
    }
  });
};

// Helper function to deselect siblings (categories at the same level)
const deselectSiblings = (categories, excludeSystemId) => {
  categories.forEach(cat => {
    if (cat.systemId !== excludeSystemId) {
      cat.isSelected = false;
      // Also deselect all children of siblings
      if (cat.links && Array.isArray(cat.links)) {
        deselectAllInTree(cat.links);
      }
    }
  });
};

// New action for category selection with exclusive selection logic
export const onCategoryOptionChanged = (filterId, optionId) => async (
  dispatch,
  getState
) => {
  try {
    const productFilteringState = getState().productFiltering;
    const brandCategoryFilters = [...productFilteringState.brandCategoryFilters]; // Create deep copy

    // Find the clicked category
    const clickedCategory = findCategoryBySystemId(brandCategoryFilters, optionId);
    if (!clickedCategory) {
      console.warn(`Category with systemId ${optionId} not found`);
      return;
    }

    const wasSelected = clickedCategory.isSelected;
    const isTopLevel = isTopLevelCategory(brandCategoryFilters, optionId);

    if (isTopLevel) {
      // Top-level category logic
      if (wasSelected) {
        // Deselect the top-level category and all its children
        clickedCategory.isSelected = false;
        if (clickedCategory.links) {
          deselectAllInTree(clickedCategory.links);
        }
      } else {
        // Deselect all other top-level categories and their children
        deselectSiblings(brandCategoryFilters, optionId);
        // Select the clicked top-level category
        clickedCategory.isSelected = true;
      }
    } else {
      // Child category logic
      const parent = findCategoryParent(brandCategoryFilters, optionId);
      if (parent) {
        if (wasSelected) {
          // Simply deselect the child category
          clickedCategory.isSelected = false;
        } else {
          // Deselect all siblings (other children of the same parent)
          if (parent.links) {
            deselectSiblings(parent.links, optionId);
          }
          // Select the clicked child category
          clickedCategory.isSelected = true;
        }
      }
    }

    // Dispatch the updated categories to the store
    dispatch({
      type: PRODUCT_FILTERING_RECEIVE_BRAND_CATEGORY_FILTERS,
      payload: brandCategoryFilters,
    });

    dispatch(fetchAndInitializeCategories());

    // Finally, query for new products
    dispatch(queryProductFiltering());

  } catch (error) {
    console.error('Failed to refresh categories:', error);
    // Still try to update products even if category refresh fails
    dispatch(queryProductFiltering());
    return [];
  }
};
